import type { CreateGoalRequest, CreateGoalResponse } from "@repo/shared/types";
import { type QueryClient, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as publicGoalsApi from "@/lib/api/public-flow/goals";
import { useSessionToken } from "@/lib/context/public-session-context";
import {
  useInvalidatePublicAssetClassForecast,
  useInvalidatePublicPortfolioSnapshots,
} from "./use-goal-snapshots";
import { useInvalidatePublicDynamicSync } from "./use-portfolios";
import { publicFlowCompletionQueryKey } from "./use-public-flow-completion";

// --- Query Keys ---

const keys = {
  goals: () => ["publicFlow", "goals"] as const,
  goal: (goalId: string | null) => ["publicFlow", "goals", goalId] as const,
  productsSuitability: (goalId: string | null) =>
    ["publicFlow", "goals", goalId, "productsSuitability"] as const,
  suitabilityContext: (goalId: string | null) =>
    ["publicFlow", "goals", goalId, "suitabilityContext"] as const,
  riskModelOptions: (goalId: string | null) =>
    ["publicFlow", "goals", goalId, "riskModelOptions"] as const,
};

// --- Query Hooks ---

export function usePublicGoals() {
  const token = useSessionToken();
  const { data, error, isLoading } = useQuery({
    queryKey: keys.goals(),
    queryFn: () => publicGoalsApi.getPublicGoals(token),
    enabled: !!token,
  });
  return { goals: data ?? [], error, isLoading };
}

export function usePublicGoal(goalId: string | null) {
  const token = useSessionToken();
  const { data, error, isLoading } = useQuery({
    queryKey: keys.goal(goalId),
    queryFn: () => publicGoalsApi.getPublicGoal(goalId!, token),
    enabled: !!token && !!goalId,
  });
  return { goal: data ?? null, error, isLoading };
}

// --- Mutation Hooks ---

export function useUpdatePublicGoal() {
  const token = useSessionToken();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ goalId, data }: { goalId: string; data: Record<string, unknown> }) =>
      publicGoalsApi.updatePublicGoal(goalId, data, token),
    onSuccess: (_result, { goalId }) => {
      queryClient.invalidateQueries({ queryKey: keys.goals() });
      queryClient.invalidateQueries({ queryKey: keys.goal(goalId) });
      queryClient.invalidateQueries({ queryKey: publicFlowCompletionQueryKey });
    },
  });
}

// §3.9 Option D — client goal management. Selection updates (which goal becomes
// active after add/delete) live in the renderer, which owns currentGoalId.
export function useAddPublicGoal() {
  const token = useSessionToken();
  const queryClient = useQueryClient();
  const invalidatePortfolioSnapshots = useInvalidatePublicPortfolioSnapshots();
  const invalidateDynamicSync = useInvalidatePublicDynamicSync();
  return useMutation({
    mutationFn: (data: CreateGoalRequest) => publicGoalsApi.addPublicGoal(data, token),
    onSuccess: (created: CreateGoalResponse) => {
      queryClient.invalidateQueries({ queryKey: keys.goals() });
      // A new goal grows the engine fan-out → refresh the completion preflight.
      queryClient.invalidateQueries({ queryKey: publicFlowCompletionQueryKey });
      // QL-212 (D13) — under a client-level risk model the goal is born scored,
      // with its model portfolio already resolved, so the same queries a risk
      // submit moves have moved here too. `pending` writes nothing and hints
      // nothing, which is every other configuration.
      const inheritance = created?.riskInheritance;
      if (inheritance?.outcome.status !== "applied") return;
      // The instance-wide aggregates sit outside the ["publicFlow","goals"]
      // prefix and fold the new goal's portfolios in. The session is the
      // instance, so match their key shape rather than threading its id here.
      queryClient.invalidateQueries({
        predicate: ({ queryKey: k }) =>
          k[0] === "publicFlow" &&
          k[1] === "instance" &&
          k[3] === "snapshot" &&
          (k[4] === "instanceCost" ||
            k[4] === "instanceExpectedValue" ||
            // QL-315 — the instance-wide asset-class mix folds in the new goal's
            // portfolios too.
            k[4] === "instanceAssetClassForecast"),
      });
      for (const hint of inheritance.refreshHints) {
        invalidatePortfolioSnapshots(hint.portfolioId, hint);
      }
      invalidateDynamicSync(inheritance.dynamicSync);
    },
  });
}

export function useDeletePublicGoal() {
  const token = useSessionToken();
  const queryClient = useQueryClient();
  const invalidateAssetClassForecast = useInvalidatePublicAssetClassForecast();
  return useMutation({
    mutationFn: (goalId: string) => publicGoalsApi.deletePublicGoal(goalId, token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys.goals() });
      // Deleting a goal shrinks the fan-out → refresh the completion preflight.
      queryClient.invalidateQueries({ queryKey: publicFlowCompletionQueryKey });
      // QL-315 — the deleted goal's own row goes with it, but the instance-wide
      // mix is regenerated server-side and has to be refetched.
      invalidateAssetClassForecast();
    },
  });
}

export function useAddPublicCustomProduct() {
  const token = useSessionToken();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      goalId,
      data,
    }: {
      goalId: string;
      data: { productId: string; amount: number; weight?: number; lang?: string };
    }) => publicGoalsApi.addPublicCustomProduct(goalId, data, token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys.goals() });
      queryClient.invalidateQueries({ queryKey: publicFlowCompletionQueryKey });
    },
  });
}

export function useUpdatePublicCustomProduct() {
  const token = useSessionToken();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      productId,
      data,
    }: {
      productId: string;
      data: { amount?: number; weight?: number; lang?: string };
    }) => publicGoalsApi.updatePublicCustomProduct(productId, data, token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys.goals() });
      queryClient.invalidateQueries({ queryKey: publicFlowCompletionQueryKey });
    },
  });
}

export function useDeletePublicCustomProduct() {
  const token = useSessionToken();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (productId: string) => publicGoalsApi.deletePublicCustomProduct(productId, token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys.goals() });
      queryClient.invalidateQueries({ queryKey: publicFlowCompletionQueryKey });
    },
  });
}
// --- Exported Query Keys ---

export { keys as publicGoalQueryKeys };

/**
 * Public-flow twin of the advisor `invalidateSuitabilityQueries`: refetch every
 * goal's suitability inputs so the client's portfolio builder reflects a changed
 * product-assessment / classification / risk verdict without a page remount.
 *
 * This is the only refresh mechanism on the public side — the `suitabilityVersion`
 * context counter the renderers used to bump was never read and has been removed
 * (QL-10).
 */
export function invalidatePublicSuitabilityQueries(queryClient: QueryClient) {
  return queryClient.invalidateQueries({
    predicate: (query) => {
      const k = query.queryKey;
      return (
        Array.isArray(k) &&
        k[0] === "publicFlow" &&
        k[1] === "goals" &&
        (k[3] === "suitabilityContext" || k[3] === "productsSuitability")
      );
    },
  });
}
